import React, { useCallback, useContext, useState } from 'react';
import classNames from 'classnames';
import { observer } from 'mobx-react';
import TableCell from '@material-ui/core/TableCell';
import TableRow from '@material-ui/core/TableRow';
import Tooltip from '@material-ui/core/Tooltip';
import PropTypes from 'prop-types';

import { AuthContext } from 'core/components/auth';
import { Label } from 'core/components/label';
import { useStore } from 'core/store/store.mobx';
import { dateTimeFormatter } from 'core/helpers/formatters';
import { Loading, SelectableRow, Table } from 'core';
import Section from 'core/components/section';
import { Card } from 'core/components/card';
import { IconButtonWithTooltip } from 'core/components/icon-button-with-tooltip';
import viewIcon from '../../../../assets/view.svg';
import { CopyInternalDocToPolicyDocsModal } from '../copy-internal-doc-to-policy-docs-modal';
import { DropzoneContainer } from 'core/components/dropzone';
import useStyles from '../../documents.styles';

const InternalDocuments = observer(
  ({
    fetchDocuments,
    sortDocuments,
    getDocName,
    downloadDocument,
    uploaded,
    uploadFile,
    percent,
    error,
    deleting,
    file
  }) => {
    const classes = useStyles();
    const [copyDocsModalOpen, setCopyDocsModalOpen] = useState(false);
    const [documentToCopy, setDocumentToCopy] = useState(null);

    const {
      account: {
        policies: { documents, loadingDocuments: loading },
        tasks
      }
    } = useStore();
    const { canUploadDocuments } = useContext(AuthContext);

    const documentToTaskMap = new Map();
    if (tasks && tasks.length > 0) {
      tasks.forEach((task) => {
        if (task.data) {
          const parsedData = typeof task.data === 'string' ? JSON.parse(task.data) : task.data;

          if (parsedData?.files) {
            parsedData.files.forEach((file) => {
              if (file?.documentUrl) {
                documentToTaskMap.set(file.documentUrl, task.taskName);
              }
            });
          }

          // Backward compatibility - support old single documentUrl field for tasks that will not
          // have a files array but still the doc will need to show
          if (parsedData?.documentUrl) {
            if (!documentToTaskMap.has(parsedData.documentUrl)) {
              documentToTaskMap.set(parsedData.documentUrl, task.taskName);
            }
          }
        }
      });
    }

    const getTaskName = useCallback(
      (documentPath) => {
        return documentToTaskMap.get(documentPath) || 'Not specified';
      },
      [documentToTaskMap]
    );

    return (
      <>
        <Section
          title="Internal Documents"
          rightLabel="Total"
          rightValue={`${loading || !documents ? '...' : documents.internal.length} documents`}
        >
          {loading || !documents ? (
            <Loading />
          ) : (
            <Card>
              {documents.internal.length || uploaded.length ? (
                <Table
                  selfContained={false}
                  header={
                    <>
                      <TableCell className={classes.fileNameHeader}>FILE NAME</TableCell>
                      <TableCell className={classes.documentTypeHeader}>DOCUMENT TYPE</TableCell>
                      <TableCell className={classes.lastUpdateHeader}>LAST UPDATE</TableCell>
                      {/* need the following empty tablecell header for the view or copy action buttons */}
                      <TableCell />
                    </>
                  }
                  body={
                    <>
                      {sortDocuments(documents.internal.slice() || []).map((d) => {
                        const fileName = getDocName(d.path);
                        const taskName = getTaskName(d.path);
                        return (
                          <TableRow key={d.path} id={d.path}>
                            <TableCell className={classes.docTitleCell}>
                              <Tooltip title={fileName} placement="top">
                                <span className={classes.truncatedText}>{fileName}</span>
                              </Tooltip>
                            </TableCell>
                            <TableCell className={classes.documentTypeCell}>
                              <Tooltip title={taskName} placement="top">
                                <span
                                  className={classNames(classes.truncatedText, {
                                    [classes.notSpecifiedText]: taskName === 'Not specified'
                                  })}
                                >
                                  {taskName}
                                </span>
                              </Tooltip>
                            </TableCell>
                            <TableCell className={classes.lastUpdateCell}>
                              <span className={classes.smallText}>
                                {dateTimeFormatter(d.lastModified).split(' @ ')[0]}
                                <br />@ {dateTimeFormatter(d.lastModified).split(' @ ')[1]}
                              </span>
                            </TableCell>
                            <TableCell>
                              <IconButtonWithTooltip
                                tooltip="View file"
                                onClick={() => downloadDocument(d.path)}
                                icon={viewIcon}
                                alt="view document"
                              />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                      {sortDocuments(uploaded).map((d) => {
                        const fileName = getDocName(d.path);
                        const taskName = getTaskName(d.path);
                        return (
                          <SelectableRow key={d.path} id={d.path} onClick={downloadDocument}>
                            <TableCell className={classNames(classes.highlighted, classes.docTitleCell)}>
                              <Tooltip title={fileName} placement="top">
                                <span className={classes.truncatedText}>{fileName}</span>
                              </Tooltip>
                            </TableCell>
                            <TableCell className={classNames(classes.highlighted, classes.documentTypeCell)}>
                              <Tooltip title={taskName} placement="top">
                                <span
                                  className={classNames(classes.truncatedText, {
                                    [classes.notSpecifiedText]: taskName === 'Not specified'
                                  })}
                                >
                                  {taskName}
                                </span>
                              </Tooltip>
                            </TableCell>
                            <TableCell className={classNames(classes.highlighted, classes.lastUpdateCell)}>
                              <span className={classes.smallText}>
                                {dateTimeFormatter(d.lastModified).split(' @ ')[0]}
                                <br />@ {dateTimeFormatter(d.lastModified).split(' @ ')[1]}
                              </span>
                            </TableCell>
                          </SelectableRow>
                        );
                      })}
                    </>
                  }
                />
              ) : (
                <p className={classes.noDocs}>No internal documents for this member.</p>
              )}
              {canUploadDocuments ? (
                <DropzoneContainer
                  onUpload={uploadFile}
                  progress={percent}
                  error={error}
                  deleting={deleting}
                  uploading={file ? file.path : undefined}
                />
              ) : (
                <div className={classes.cantUploadContainer}>
                  <Label className={classes.cantUpload}>
                    To provide a document for this member, please send the document to Branch at{' '}
                    <a className={classes.support} href="mailto:support@ourbranch.com" rel="noopener noreferrer">
                      support@ourbranch.com
                    </a>
                  </Label>
                </div>
              )}
            </Card>
          )}
        </Section>
        {copyDocsModalOpen && (
          <CopyInternalDocToPolicyDocsModal
            open={copyDocsModalOpen}
            getDocName={getDocName}
            document={documentToCopy}
            refetchDocuments={fetchDocuments}
            onClose={() => {
              setCopyDocsModalOpen(false);
            }}
          />
        )}
      </>
    );
  }
);

InternalDocuments.propTypes = {
  sortDocuments: PropTypes.func.isRequired,
  fetchDocuments: PropTypes.func.isRequired,
  getDocName: PropTypes.func.isRequired,
  downloadDocument: PropTypes.func.isRequired,
  uploaded: PropTypes.array,
  uploadFile: PropTypes.func.isRequired,
  percent: PropTypes.number,
  error: PropTypes.string,
  deleting: PropTypes.bool,
  file: PropTypes.object
};

InternalDocuments.defaultProps = {
  percent: 0,
  uploaded: [],
  deleting: false,
  error: undefined,
  file: undefined
};

export default InternalDocuments;
